import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import {
  type ActiveAssignment,
  type AssignmentSummary,
  type ContextType,
  getPreviewContextType,
  getTabAssignment,
  subscribePreviewContextType,
  withActiveContextType,
} from '@/auth/activeAssignment';
import {
  type BootOutcome,
  type PermissionsMap,
  type SessionModules,
  type SessionUser,
  getBootOutcome,
  getPermissionsMap,
  getSessionModules,
  getSessionUser,
  isSignedIn,
  readSessionData,
  subscribeBootOutcome,
  subscribeSession,
} from '@/auth/session';
import { bootSession, retryBootSession } from '@/auth/sessionBoot';
import { listenForSessionSignals } from '@/auth/roleChange';
import { sessionKeys } from '@/query/keys';

/**
 * The session for React (UI state plan, phase 1): who is signed in, which
 * role is active and what it may do.
 *
 * The session IS server data, so there is no store of its own: it lives in
 * the TanStack Query cache (auth/session.ts, in memory only) and
 * `useSession()` follows those cache entries. Everything that changes the
 * session (sign-in, role switch, the hard-refresh load, sign-out) writes the
 * cache; every component using the hook re-renders.
 *
 * Code that cannot use a hook (the static ApiProvider, auth/*.ts) reads the
 * same entries through auth/session.ts and auth/activeAssignment.ts.
 */
export type SessionStatus =
  /** the page load is still asking who is signed in */
  | 'loading'
  | 'signed-in'
  | 'signed-out'
  /** a token exists but the session did not load: no answer, a server error, a rate limit (retry) */
  | 'unavailable';

export interface SessionValue {
  status: SessionStatus;
  /** The signed-in user; `type` follows the active role's context type. */
  user: SessionUser | null;
  /** The active role assignment, once the server confirmed it. */
  assignment: ActiveAssignment | null;
  /** This user's role assignments (the switcher's list). */
  assignments: AssignmentSummary[];
  /** The previewed role's context type while previewing, else the active assignment's. */
  contextType: ContextType | null;
  /** The active role's permission map is in memory. */
  permissionsLoaded: boolean;
  /** True only when the active role holds `key`. */
  hasPermission: (key: string) => boolean;
  /** The landing modules of the active role (identity me/details). */
  modules: SessionModules | null;
}

const NO_ASSIGNMENTS: AssignmentSummary[] = [];

type Parts = [
  SessionUser | null,
  ActiveAssignment | null,
  AssignmentSummary[] | undefined,
  PermissionsMap | null,
  SessionModules | null,
  ContextType | null,
  BootOutcome | null,
];

let lastParts: Parts | null = null;
let lastValue: SessionValue | null = null;
let lastUserParts: [SessionUser | null, ActiveAssignment | null, ContextType | null] | null = null;
let lastUser: SessionUser | null = null;

/** The user with `type` = the active context type; the same object until one of the three changes. */
const shimmedUser = (
  rawUser: SessionUser | null,
  assignment: ActiveAssignment | null,
  previewType: ContextType | null,
): SessionUser | null => {
  if (
    lastUserParts &&
    lastUserParts[0] === rawUser &&
    lastUserParts[1] === assignment &&
    lastUserParts[2] === previewType
  ) {
    return lastUser;
  }
  lastUserParts = [rawUser, assignment, previewType];
  lastUser = rawUser ? withActiveContextType(rawUser, assignment) : null;
  return lastUser;
};

/**
 * The session as one value, built from the cache entries. The same object is
 * returned until one of the entries changes (useSyncExternalStore needs that).
 */
export const getSessionSnapshot = (): SessionValue => {
  const parts: Parts = [
    getSessionUser(),
    getTabAssignment(),
    readSessionData<AssignmentSummary[]>(sessionKeys.assignments),
    getPermissionsMap(),
    getSessionModules(),
    getPreviewContextType(),
    getBootOutcome(),
  ];
  if (lastValue && lastParts && parts.every((part, i) => part === lastParts![i])) {
    return lastValue;
  }
  const [rawUser, assignment, list, permissions, modules, previewType, outcome] = parts;
  const status: SessionStatus = rawUser
    ? 'signed-in'
    : outcome === null
      ? 'loading'
      : outcome === 'unavailable'
        ? 'unavailable'
        : 'signed-out';
  lastParts = parts;
  lastValue = {
    status,
    user: shimmedUser(rawUser, assignment, previewType),
    assignment,
    assignments: Array.isArray(list) ? list : NO_ASSIGNMENTS,
    contextType: assignment ? (previewType ?? assignment.contextType) : previewType,
    permissionsLoaded: permissions !== null,
    hasPermission: (key: string) => permissions?.[key] === true,
    modules,
  };
  return lastValue;
};

const subscribeAll = (listener: () => void): (() => void) => {
  const stops = [
    subscribeSession(listener),
    subscribePreviewContextType(listener),
    subscribeBootOutcome(listener),
  ];
  return () => stops.forEach((stop) => stop());
};

/**
 * The session. Re-renders when the user, the active role, its permissions or
 * its modules change. It reads the query cache directly, so it also works in
 * a component rendered without <SessionProvider> (isolated tests).
 */
export const useSession = (): SessionValue =>
  useSyncExternalStore(subscribeAll, getSessionSnapshot, getSessionSnapshot);

/** `hasPermission(key)` of the active role, as a hook. */
export const useHasPermission = (key: string): boolean => useSession().hasPermission(key);

/** The active role assignment, or null. */
export const useActiveAssignment = (): ActiveAssignment | null => useSession().assignment;

export interface SessionBootValue {
  /** The page load finished asking who is signed in. */
  booted: boolean;
  outcome: BootOutcome | null;
  retry: () => void;
}

const retryBoot = (): void => {
  void retryBootSession();
};

/**
 * Whether the page load finished loading the session. Separate from
 * `useSession()` on purpose: the app is gated on this alone, so a sign-in or
 * sign-out in progress does not re-render the whole app under the flow that
 * runs it.
 */
export const useSessionBoot = (): SessionBootValue => {
  const outcome = useSyncExternalStore(subscribeBootOutcome, getBootOutcome, getBootOutcome);
  return useMemo(() => ({ booted: outcome !== null, outcome, retry: retryBoot }), [outcome]);
};

/**
 * A token exists but the page-load session check got no answer: the identity
 * service (who is signed in) or the gateway (the active role's permission map).
 */
const SessionUnavailable = ({ onRetry }: { onRetry: () => void }) => (
  <div className='main-loader-area' data-testid='session-unavailable' role='alert'>
    <div style={{ textAlign: 'center', padding: '24px' }}>
      <p>Your session could not be loaded. Please try again.</p>
      <button type='button' className='btn btn-primary' onClick={onRetry}>
        Retry
      </button>
    </div>
  </div>
);

/**
 * Mounted once at the root, inside <PnetAuthProvider> (Amplify is configured
 * there) and <QueryClientProvider>, around the rest of the app.
 *
 * On a page load it asks who is signed in and loads the active role's session
 * (auth/sessionBoot.ts), and renders the app only when that answered: the
 * session is in memory only, and the app's providers and pages read it while
 * they mount (as they read browser storage before). Until then `fallback`
 * (the page loader) is shown. It also listens for a role switch or a sign-out
 * in another tab of this browser (auth/roleChange.ts).
 */
export const SessionProvider = ({
  children,
  fallback = <div className='main-loader-area' data-testid='session-loading' />,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const boot = useSessionBoot();

  useEffect(() => {
    void bootSession();
  }, []);

  useEffect(() => listenForSessionSignals(), []);

  if (!boot.booted) return <>{fallback}</>;
  // the user, or the active role's permission map, did not load: retry, never a
  // page of "No permission" for a user who holds it
  if (boot.outcome === 'unavailable' && (!isSignedIn() || getPermissionsMap() === null)) {
    return <SessionUnavailable onRetry={boot.retry} />;
  }
  return <>{children}</>;
};
